import { BaseAction } from "../../../../core/actions/BaseAction";
import { determineIsDOBRequiredForPassengerType, determinePassengerType } from "../../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, getRecoilStateValueWithType, updateRecoilState } from "../../../../Orchestrator/Recoils/Events/Recoil";
import { passegerTypeEnum } from "../../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IDeterminePassenger, IPassenger, IUserResponse } from "../../../../Orchestrator/Types/Index";
import { PassengerSelectReqType } from "../../../../Orchestrator/Types/Types/Passenger/PassengerType";

export class SelectPassengerAction extends BaseAction<void, PassengerSelectReqType> {

    override async run(): Promise<void | null> {
        try {
            const { passengerTypeIndex, passengerId, passengers, passengerType, savedPassenger, selectedPassengersPerType, passengerMapping,
                savedPassengerDetail, booking, isHyperFast, totalPassengers, isChangeDate, isManageSeating, addonsPassengerList, confirmAddonsPassengerList, isBookReturnJourney, isMultiBooking
            } = this.params!;

            // Review this and update logic for this
            const appliedPassengerPassAtom = getRecoilStateValueWithType("appliedPassengerPassAtom")
            const passengerRefs = passengers.map(p => p.externalRef)
            const outboundPassCards = appliedPassengerPassAtom?.outbound?.filter(pass => passengerRefs.includes(pass.externalRef)) || []
            const inboundPassCards = appliedPassengerPassAtom?.inbound?.filter(pass => passengerRefs.includes(pass.externalRef)) || []
            const isPassBooking = outboundPassCards.length > 0 || inboundPassCards.length > 0;
            const handelPassBookingFlow = isPassBooking && passengerId === "initial_auto_select"

            const typeIndex = Math.floor(passengerTypeIndex / 10);
            const subIndex = passengerTypeIndex % 10;
            const passengerKey = `${typeIndex}-${subIndex}`;
            const currentPassengerId = selectedPassengersPerType[passengerKey];

            // Temporarily store updates to minimize inconsistencies
            let updatedPassengers = [...passengers];
            let updatedSelectedPassengersPerType = { ...selectedPassengersPerType };

            // When the manage-seating / hyperFast / book-return / change-date /
            // pass-booking branch below receives a saved-passenger pick, it
            // merges the savedPassenger's firstName/lastName onto the slot's
            // existing passenger in place. Skip the "remove current passenger"
            // pre-step in that case — otherwise the target passenger would be
            // stripped from updatedPassengers before the merge can find it,
            // the merge would silently no-op, and passengerStateAtom would
            // shrink (losing other slots bound to the same list).
            const isManageSeatingMergeSelect =
                (isHyperFast || isBookReturnJourney || handelPassBookingFlow) &&
                (isChangeDate || isManageSeating || isBookReturnJourney || handelPassBookingFlow) &&
                (passengerId === "saved_passenger" || passengerId === "initial_auto_select") &&
                !!savedPassenger;

            // Remove the current passenger if exists
            if (currentPassengerId) {
                if (!isManageSeatingMergeSelect) {
                    updatedPassengers = updatedPassengers.filter(
                        (p) => p.id !== currentPassengerId
                    );
                    updatedSelectedPassengersPerType[passengerKey] = null;
                }

                // Only reset the isSelected state for the current passenger slot
                if (passengerMapping.some((entry) => entry.selectedPassengerTypeIndex === String(passengerTypeIndex))
                ) {
                    const currentSavedPassengerId = passengerMapping?.find(
                        (entry) =>
                            entry.selectedPassengerTypeIndex === String(passengerTypeIndex)
                    )?.savedPassenger;

                    updateRecoilState("savedPassengerDetailAtom",
                        savedPassengerDetail.map((savedP) => ({
                            ...savedP,
                            isSelected:
                                savedP.id === currentSavedPassengerId ? false : savedP.isSelected,
                        }))
                    )

                    updateRecoilState("passengerMappingAtom",
                        passengerMapping.filter(
                            (entry) =>
                                entry.selectedPassengerTypeIndex !== String(passengerTypeIndex)
                        )
                    )
                }
            }

            if (savedPassenger?.id) {
                const updatedMapping = [...passengerMapping];
                const existingIndex = updatedMapping.findIndex(
                    (entry) =>
                        entry.selectedPassengerTypeIndex === String(passengerTypeIndex)
                );

                if (existingIndex !== -1) {
                    //updatedMapping[existingIndex].savedPassenger = savedPassenger.id;
                    updatedMapping[existingIndex] = {
                        selectedPassengerTypeIndex: String(passengerTypeIndex),
                        savedPassenger: savedPassenger.id,
                    };
                } else {
                    updatedMapping.push({
                        selectedPassengerTypeIndex: String(passengerTypeIndex),
                        savedPassenger: savedPassenger.id,
                    });
                }

                updateRecoilState("savedPassengerDetailAtom",
                    savedPassengerDetail.map((savedP) => {
                        const isSelectedForIndex = updatedMapping.some(
                            (entry) =>
                                entry.selectedPassengerTypeIndex === String(passengerTypeIndex) &&
                                entry.savedPassenger === savedP.id
                        );

                        return {
                            ...savedP,
                            isSelected: isSelectedForIndex,
                        };
                    })
                );

                updateRecoilState("passengerMappingAtom", updatedMapping)
            }

            const savedPassengerIds = ["saved_passenger", "initial_auto_select"];

            // Handle new selection
            if (
                passengerId === "add_new" ||
                (savedPassengerIds.includes(passengerId) && savedPassenger)
            ) {
                // Handle multi-booking: update existing passengers from booking response instead of creating new ones
                if (isMultiBooking) {
                    // Get the latest passenger state from atom to preserve email/phoneNumber updates
                    const latestPassengers = getRecoilStateValue<IPassenger[]>("passengerStateAtom") || passengers;
                    // Get customerContact for initial prefill (first time only)
                    const customerContact = getRecoilStateValue<any>("customerContactStateAtom") || {};
                    
                    const passengerTypes = Object.keys(totalPassengers.passengerCounts);
                    const typeCounters: Record<string, number> = {};
                    const usedBookingPassengerIds: Set<string> = new Set();
                    passengerTypes.forEach(type => {
                        typeCounters[type] = 0;
                    });

                    // Get existing passengers from booking response - this is the source of truth
                    const bookingPassengers = booking?.booking?.passengers || [];
                    
                    // Rebuild passengers list from booking response, merging any user-entered details
                    updatedPassengers = [];
                    updatedSelectedPassengersPerType = {};
                    
                    const currentSlotKey = `${typeIndex}-${subIndex}`;
                    
                    // First, build a map of booking passengers by their determined type and index
                    // This ensures we match the correct booking passenger to each slot
                    const bookingPassengersByType: Record<string, IPassenger[]> = {};
                    bookingPassengers.forEach((bp: IPassenger) => {
                        const bpDeterminePassenger: IDeterminePassenger = {
                            age: bp?.age,
                            cards: bp?.cards || [],
                            prmNeeds: ((bp?.prmNeeds || []).length > 0 && bp?.prmNeeds) ? bp?.prmNeeds[0] : ""
                        };
                        const bpPassengerType = determinePassengerType(bpDeterminePassenger);
                        if (!bookingPassengersByType[bpPassengerType]) {
                            bookingPassengersByType[bpPassengerType] = [];
                        }
                        bookingPassengersByType[bpPassengerType].push(bp);
                    });
                    
                    // Process all passenger types and their slots
                    passengerTypes.forEach((passType, typeIdx) => {
                        const count = totalPassengers.passengerCounts[passType];
                        for (let subIdx = 0; subIdx < count; subIdx++) {
                            const slotKey = `${typeIdx}-${subIdx}`;
                            const isCurrentSlot = slotKey === currentSlotKey;
                            
                            // Find the booking passenger for this slot
                            let targetBookingPassenger: IPassenger | undefined;
                            
                            if (isCurrentSlot && savedPassenger && passengerType === passType) {
                                // For current slot with savedPassenger, find booking passenger matching the passengerType
                                const availablePassengers = bookingPassengersByType[passengerType] || [];
                                targetBookingPassenger = availablePassengers.find(bp => !usedBookingPassengerIds.has(bp.id));
                            } else {
                                // For other slots, find booking passenger matching the determined type
                                const availablePassengers = bookingPassengersByType[passType] || [];
                                targetBookingPassenger = availablePassengers.find(bp => !usedBookingPassengerIds.has(bp.id));
                            }
                            
                            if (targetBookingPassenger) {
                                usedBookingPassengerIds.add(targetBookingPassenger.id);
                                
                                // Find existing passenger from latest state to preserve user-entered details (including email/phoneNumber)
                                const existingPassenger = latestPassengers.find(
                                    p => p.id === targetBookingPassenger!.id || p.externalRef === targetBookingPassenger!.externalRef
                                );
                                
                                // Use savedPassenger data for current slot, otherwise use existing passenger details
                                // For email/phoneNumber: prioritize existing passenger (preserves user updates), then customerContact (initial prefill), then booking passenger
                                const firstName = (isCurrentSlot && savedPassenger?.firstName) 
                                    ? savedPassenger.firstName 
                                    : existingPassenger?.detail?.firstName || targetBookingPassenger?.detail?.firstName || "";
                                const lastName = (isCurrentSlot && savedPassenger?.lastName) 
                                    ? savedPassenger.lastName 
                                    : existingPassenger?.detail?.lastName || targetBookingPassenger?.detail?.lastName || "";
                                // Email and phoneNumber: prioritize existing passenger (to preserve user updates), then customerContact (initial prefill), then booking passenger
                                const email = existingPassenger?.detail?.email || customerContact?.email || targetBookingPassenger?.detail?.email || "";
                                const phoneNumber = existingPassenger?.detail?.phoneNumber || customerContact?.phoneNumber || targetBookingPassenger?.detail?.phoneNumber || "";
                                const isDOBRequired = determineIsDOBRequiredForPassengerType(passType);
                                const dateOfBirth = (isCurrentSlot && savedPassenger?.birthdate && isDOBRequired)
                                    ? new Date(savedPassenger.birthdate)
                                    : existingPassenger?.dateOfBirth || (targetBookingPassenger.dateOfBirth ? new Date(targetBookingPassenger.dateOfBirth) : undefined);
                                
                                const mergedPassenger: IPassenger = {
                                    ...targetBookingPassenger,
                                    detail: {
                                        firstName: firstName,
                                        lastName: lastName,
                                        email: email,
                                        phoneNumber: phoneNumber,
                                        age: targetBookingPassenger?.age,
                                    },
                                    age: targetBookingPassenger?.age, // Age from booking response
                                    cards: targetBookingPassenger.cards || [],
                                    prmNeeds: targetBookingPassenger.prmNeeds || [],
                                    type: "PERSON",
                                    remember: existingPassenger?.remember ?? false,
                                    seat: existingPassenger?.seat || targetBookingPassenger.seat || "",
                                    dateOfBirth: dateOfBirth,
                                };
                                
                                updatedPassengers.push(mergedPassenger);
                                updatedSelectedPassengersPerType[slotKey] = targetBookingPassenger.id;
                            }
                        }
                    });
                } else if (isHyperFast || isBookReturnJourney || handelPassBookingFlow) {
                    const passengerTypes = Object.keys(totalPassengers.passengerCounts); // ['adults', 'infants', 'children']
                    const typeCounters: Record<string, number> = {}; // To keep track of index per type
                    passengerTypes.forEach(type => {
                        typeCounters[type] = 0;
                    });

                    if (isChangeDate || isManageSeating || isBookReturnJourney || handelPassBookingFlow) {
                        // When isChangeDate is true, `passengers` (from this.params) is already the correct,
                        // filtered list from `passengerStateAtom`.
                        // `updatedPassengers` (initialized from `this.params.passengers`) is also correct.
                        // We only need to rebuild `updatedSelectedPassengersPerType` based on these existing passengers.
                        // Do not modify `updatedPassengers` or clear `passengerStateAtom` here.

                        // Bail out when the caller's passenger list is empty: rebuilding from [] would
                        // write an empty array back to passengerStateAtom and wipe values that
                        // useRecState just restored from localStorage, triggering the auto-select
                        // effect to re-emit add_new and loop indefinitely on /checkout refresh.
                        if (updatedPassengers.length === 0) {
                            return;
                        }

                        // When the user picked a saved passenger from the dropdown
                        // (backend names were missing and the user chose one), apply
                        // its firstName/lastName onto the slot's target passenger.
                        // Without this, clicking a dropdown entry would not update
                        // the passenger card at all.
                        if (savedPassengerIds.includes(passengerId) && savedPassenger) {
                            // Target: the passenger currently bound to this slot,
                            // or (if unbound) the positional Nth booking passenger.
                            let targetIndex = -1;
                            if (currentPassengerId) {
                                targetIndex = updatedPassengers.findIndex(
                                    (p) => p.id === currentPassengerId
                                );
                            }
                            if (targetIndex === -1) {
                                // Compute the slot's global position from the order
                                // of passengerCounts. Matches the UI's sortedPassengerTypes
                                // iteration by relying on Object.keys insertion order.
                                let globalIndex = 0;
                                for (let t = 0; t < typeIndex; t++) {
                                    const typeName = passengerTypes[t];
                                    globalIndex += (totalPassengers.passengerCounts[typeName] || 0);
                                }
                                globalIndex += subIndex;
                                if (globalIndex >= 0 && globalIndex < updatedPassengers.length) {
                                    targetIndex = globalIndex;
                                }
                            }
                            if (targetIndex !== -1) {
                                const existing = updatedPassengers[targetIndex];
                                const mergedDetail = {
                                    ...existing.detail,
                                    firstName: savedPassenger.firstName || existing.detail?.firstName || "",
                                    lastName: savedPassenger.lastName || existing.detail?.lastName || "",
                                };
                                const mergedDob = (savedPassenger.birthdate && determineIsDOBRequiredForPassengerType(passengerType || ""))
                                    ? new Date(savedPassenger.birthdate)
                                    : existing.dateOfBirth;
                                updatedPassengers[targetIndex] = {
                                    ...existing,
                                    detail: mergedDetail,
                                    dateOfBirth: mergedDob,
                                };
                            }
                        }

                        updatedSelectedPassengersPerType = {}; // Reset to rebuild

                        // Positional mapping: passenger N -> slot N in the UI's
                        // sortedPassengerTypes iteration order. Avoids the
                        // determinePassengerType range-ordering bug
                        // (src/Lib/Utils/validationUtil.ts) where adults 17-150
                        // is checked before seniorcitizens 60-140, causing seniors
                        // to be mis-classified as adults and their slot to stay
                        // empty forever. Relies on the backend returning
                        // booking.passengers in the same order the UI lays out
                        // slots — true for the manage-seating/hyperFast flows
                        // this branch handles.
                        let globalIdx = 0;
                        passengerTypes.forEach((typeName, typeIdx) => {
                            const count = totalPassengers.passengerCounts[typeName] || 0;
                            for (let subIdx = 0; subIdx < count; subIdx++) {
                                if (globalIdx < updatedPassengers.length) {
                                    const slotKey = `${typeIdx}-${subIdx}`;
                                    updatedSelectedPassengersPerType[slotKey] = updatedPassengers[globalIdx].id;
                                    globalIdx++;
                                }
                            }
                        });
                        // `updatedPassengers` remains as it was (the correct filtered list).
                        // typeCounters is no longer needed here but kept declared above
                        // because the outer block's other branches still use it.
                        void typeCounters;
                    } else {
                        // Original hyperFast logic (not isChangeDate)
                        // Clear state and rebuild passengers from booking.booking.passengers
                        updateRecoilState("passengerStateAtom", null);
                        updatedPassengers = []; // Reset local list to empty
                        updatedSelectedPassengersPerType = {}; // Reset local selections to empty

                        const sourceBookingPassengers = booking?.booking?.passengers;
                        if (sourceBookingPassengers) {
                            sourceBookingPassengers.forEach((bookingPassenger: IPassenger) => {
                                const determinePassenger: IDeterminePassenger = {
                                    age: bookingPassenger?.age,
                                    cards: bookingPassenger?.cards || [],
                                    prmNeeds: ((bookingPassenger?.prmNeeds || []).length > 0 && bookingPassenger?.prmNeeds) ? bookingPassenger?.prmNeeds[0] : ""
                                };
                                const passengerType = determinePassengerType(determinePassenger);
                                const typeIndexVal = passengerTypes.indexOf(passengerType);

                                if (typeIndexVal !== -1) {
                                    const index = typeCounters[passengerType]++;
                                    const passengerKey = `${typeIndexVal}-${index}`;

                                    const newPassenger: IPassenger = {
                                        id: bookingPassenger.id,
                                        detail: {
                                            firstName: bookingPassenger?.detail?.firstName || "",
                                            lastName: bookingPassenger?.detail?.lastName || "",
                                            email: bookingPassenger?.detail?.email || "",
                                            age: bookingPassenger?.age,
                                        },
                                        externalRef: bookingPassenger.externalRef,
                                        type: "PERSON",
                                        cards: bookingPassenger.cards || [],
                                        prmNeeds: bookingPassenger.prmNeeds || [],
                                        remember: true,
                                        age: bookingPassenger?.age,
                                        dateOfBirth: bookingPassenger.dateOfBirth ? new Date(bookingPassenger.dateOfBirth) : undefined,
                                        seat: bookingPassenger.seat || "",
                                    };
                                    updatedPassengers.push(newPassenger);
                                    updatedSelectedPassengersPerType[passengerKey] = newPassenger.id;
                                }
                            });
                        }
                    }
                } else {
                    // Original logic for non-hyperfast case
                    const neededType = passengerType;
                    const availablePassenger = booking?.booking?.passengers?.find(
                        (p) => {
                            //let adjustedAge = p.age >= 60 ? 60 : p.age;
                            const isInfantType = neededType == passegerTypeEnum.infants;
                            const isChildrenType = neededType === passegerTypeEnum.children;
                            // /**
                            //  * Below if condition is because,
                            //  * If first passenger is contact information passenger and if that passenger have birthdate,
                            //  * Then age will be based on that passenger birthdate and that contact information passenger consider as adult.
                            //  * Now @method determineAge return 16 age for adult but our actual age is based on birthdate,
                            //  * So here we need to add condition that if contact passenger age is diff then 16 then set 16 to match record into @var availablePassenger 
                            // */
                            // const determinePassenger: IDeterminePassenger = {
                            //     age: p.age || 0,
                            //     cards: p?.cards || [],
                            //     prmNeeds: ((p?.prmNeeds || []).length > 0 && p?.prmNeeds) ? p?.prmNeeds[0] : ""
                            // }
                            // const passengerType = determinePassengerType(determinePassenger)
                            // if (passengerType === "adults" && p.age !== 12) {
                            //     adjustedAge = 12
                            // }

                            const determinePassenger: IDeterminePassenger = {
                                age: typeof p?.age === "number" ? p.age : undefined,
                                cards: Array.isArray(p?.cards) ? p.cards : [],
                                prmNeeds: ((p?.prmNeeds || []).length > 0 && p?.prmNeeds) ? p?.prmNeeds[0] : ""
                            };
                            const derivedType = determinePassengerType(determinePassenger);

                            return (
                                !Object.values(updatedSelectedPassengersPerType).includes(p.id) &&
                                derivedType === (neededType || "") &&
                                (!isInfantType || (Array.isArray(p.cards) && p.cards.length > 0)) &&
                                (!isChildrenType || !(Array.isArray(p.cards) && p.cards.length > 0))
                            );
                        }
                    );

                    if (!availablePassenger) return;

                    const newPassenger: IPassenger = {
                        id: availablePassenger.id,
                        detail: {
                            firstName:
                                savedPassengerIds.includes(passengerId) && savedPassenger
                                    ? savedPassenger?.firstName
                                    : "",
                            lastName:
                                savedPassengerIds.includes(passengerId) && savedPassenger
                                    ? savedPassenger?.lastName
                                    : "",
                            email: "",
                            age: availablePassenger.age,
                        },
                        externalRef: availablePassenger.externalRef,
                        type: "PERSON",
                        cards: availablePassenger.cards || [],
                        prmNeeds: availablePassenger.prmNeeds || [],
                        remember: savedPassengerIds.includes(passengerId) ? false : true,
                        age: availablePassenger.age,
                        dateOfBirth: (savedPassenger?.birthdate && determineIsDOBRequiredForPassengerType(neededType || ""))
                            ? new Date(savedPassenger.birthdate)
                            : undefined,
                        seat: "",
                    };

                    // Replace an existing entry for the same booking passenger
                    // id — PassengerDetails seeds passengerStateAtom from
                    // booking.booking.passengers (empty firstName/lastName)
                    // on mount, so a plain push would leave the seeded empty
                    // entry ahead of the enriched one. passengers.find(id) at
                    // render time would then return the empty entry and the
                    // dropdown button would fall back to "Add new passenger".
                    const existingIdx = updatedPassengers.findIndex(
                        (p) => p.id === newPassenger.id
                    );
                    if (existingIdx !== -1) {
                        updatedPassengers[existingIdx] = newPassenger;
                    } else {
                        updatedPassengers.push(newPassenger);
                    }
                    updatedSelectedPassengersPerType[passengerKey] = newPassenger.id;
                }
            }
            
            if (savedPassenger) {  
                const loggedUserDetails: Partial<IUserResponse> = getRecoilStateValue("userDataAtom") || {}              
                const updateAddonsPassenger = addonsPassengerList?.map(({ ...leg }) => {
                    if (leg?.addonsPassenger) {
                        const confirmLeg = confirmAddonsPassengerList?.find(d => d.legId === leg.legId)
                        leg.addonsPassenger = leg.addonsPassenger.map(({ ...p }, index) => {                            
                            const confirmPass = confirmLeg?.addonsPassenger?.find(d => d.passengerId === p.passengerId)
                            if (p.passengerId === currentPassengerId) {
                                p = {
                                    ...p,
                                    isActive: index === 0,
                                    passengerName: `${savedPassenger?.firstName || ""} ${savedPassenger?.lastName || ""}`,
                                    isMePassenger: (loggedUserDetails?.firstName === savedPassenger?.firstName && loggedUserDetails?.lastName === savedPassenger?.lastName)
                                }
                            }
                            if (confirmPass) {
                                p.addonItem = confirmPass?.addonItem || {}
                            }
                            return p
                        })
                    }
                    return leg
                })
                if ((updateAddonsPassenger || []).length > 0) {
                    
                    updateRecoilState("addonsPassengerListAtom", updateAddonsPassenger)
                    updateRecoilState("confirmAddonsPassengerListAtom", updateAddonsPassenger)
                }
            }

            // Apply updates to Recoil state
            updateRecoilState("passengerStateAtom", updatedPassengers);
            updateRecoilState("selectedPassengersPerTypeStateAtom", updatedSelectedPassengersPerType)

        } catch (error) {
            console.error("Error in SelectPassengerAction:", error);
        }
    }
}

// Create a singleton instance
export const selectPassengerAction = new SelectPassengerAction(); 